import { Component, Input, OnChanges, OnDestroy, SimpleChanges, input, signal } from '@angular/core';

@Component({
  selector: 'app-transcription-progress-indicator',
  standalone: true,
  imports: [],
  templateUrl: './transcription-progress-indicator.component.html',
  styleUrls: ['../../waveform.scss'],
})
export class TranscriptionProgressIndicatorComponent implements OnChanges, OnDestroy {
  @Input() isActive: boolean = false;

  barsCount = input<number>(60);
  fullWidth = input<boolean>(false);

  public waveformBars = signal<{ height: number }[]>([]);

  private animationIntervalId: number | null = null;
  private phase = 0; // A counter used to animate the sine wave over time

  constructor() {
    this.waveformBars.set(Array(this.barsCount()).fill({ height: 1 }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isActive']) {
      if (this.isActive) {
        this.startAnimation();
      } else {
        this.stopAnimation();
      }
    }
  }

  ngOnDestroy(): void {
    this.stopAnimation();
  }

  private startAnimation(): void {
    if (this.animationIntervalId) return; // Don't start if already running

    // Use setInterval to repeatedly call the update method, creating the animation.
    this.animationIntervalId = window.setInterval(() => {
      this.updateWaveform();
    }, 100); // Update the waveform every 100 milliseconds
  }

  private stopAnimation(): void {
    if (this.animationIntervalId) {
      clearInterval(this.animationIntervalId);
      this.animationIntervalId = null;
    }
    // Reset the bars to a flat line when the animation is not active.
    this.waveformBars.set(Array(this.barsCount()).fill({ height: 1 }));
  }

  /**
   * Calculates the new height for each bar using a sine wave to create a
   * smooth, rhythmic "breathing" or "pulsing" effect.
   */
  private updateWaveform(): void {
    const newBars = [];
    // Increment the phase on each frame to make the wave appear to move.
    this.phase += 0.2;

    for (let i = 0; i < this.barsCount(); i++) {
      // The sine wave's phase is offset for each bar, creating the wave shape.
      const sinValue = Math.sin(this.phase + i / (this.barsCount() / (2 * Math.PI)));

      // A sine wave's output is from -1 to 1. We normalize it to a 0-1 range.
      const normalizedHeight = (sinValue + 1) / 2;

      // Map the normalized value to a percentage height for the bar.
      const height = normalizedHeight * 99 + 1; // Results in a height between 2% and 100%

      newBars.push({ height: height });
    }
    this.waveformBars.set(newBars);
  }
}
